<div class="content-info swo-page">

  <!-- ── Sticky document header ─────────────────────────────────────── -->
  <div class="zf-doc-head">
    <div class="zf-doc-left">
      <button type="button" class="zf-doc-back" (click)="goToList()">
        <i class="bi bi-arrow-left"></i> Write-offs
      </button>
      <div class="zf-doc-title-row">
        <h3 class="zf-doc-title">
          {{ isViewMode ? 'View Stock Write Off' : (isEditMode ? 'Edit Stock Write Off' : 'Create Stock Write Off') }}
        </h3>
        @if (payload.writeOffNumber) {
        <span class="zf-doc-no"
          [title]="isEditMode || isViewMode ? '' : 'Provisional — the final number is assigned when you save'">
          {{ payload.writeOffNumber }}
        </span>
        }
        @if (selectedLocationName) {
        <span class="zf-status info"><i class="bi bi-geo-alt"></i>{{ selectedLocationName }}</span>
        }
        @if (isViewMode) {
        <span class="zf-status dot">Read only</span>
        } @else {
        <span class="zf-status dot" [class.warn]="payload.status === 'DRAFT'" [class.ok]="payload.status === 'POSTED'"
          [class.danger]="payload.status === 'CANCELLED'">{{ payload.status || 'DRAFT' }}</span>
        }
      </div>
    </div>

    @if (!isViewMode) {
    <div class="zf-doc-actions">
      <button type="button" class="btn btn-secondary" (click)="downloadTemplate()"
        title="Download the write-off upload template">
        <i class="bi bi-download"></i> Download template
      </button>
      <span class="zf-vsep"></span>
      <button type="button" class="btn btn-secondary" (click)="saveDraft()"
        [disabled]="savingDraft || loading || !displayItems.length">
        <i class="bi bi-pencil-square"></i>
        @if (!savingDraft) { <span>Save draft</span> }
        @else { <span>{{ isEditMode ? "Updating Draft..." : "Saving Draft..." }}</span> }
      </button>
      <button type="button" class="btn zf-btn-hero" (click)="submitWriteOff()"
        [disabled]="loading || savingDraft || !displayItems.length">
        <i class="bi bi-save"></i>
        @if (!loading) { <span>Submit write-off</span> }
        @else { <span>Submitting...</span> }
      </button>
    </div>
    }
  </div>

  <!-- ── Write-off header ───────────────────────────────────────────── -->
  <div class="zf-doc-section">
    <div class="zf-section-head collapsible" [class.closed]="!headerOpen" (click)="headerOpen = !headerOpen">
      <span class="zf-section-title">Write-off header</span>
      <div class="zf-sum-chips">
        <span class="zf-sum-chip" [class.muted]="!selectedLocationName">
          <span class="k">Location</span>{{ selectedLocationName || 'Not selected' }}
        </span>
        <span class="zf-sum-chip">
          <span class="k">No.</span><span class="mono">{{ payload.writeOffNumber || '—' }}</span>
        </span>
        @if (payload.notes) {
        <span class="zf-sum-chip" [title]="payload.notes"><span class="k">Notes</span>{{ payload.notes }}</span>
        }
        @if (displayItems.length) {
        <span class="zf-sum-chip primary">{{ lineCountLabel }} · {{ getTotalQuantity() | number:'1.0-3' }} units</span>
        }
      </div>
      <span class="zf-section-toggle">
        {{ headerOpen ? 'Collapse header' : 'Edit header' }}
        <i class="bi" [class.bi-chevron-up]="headerOpen" [class.bi-chevron-down]="!headerOpen"></i>
      </span>
    </div>

    @if (headerOpen) {
    <div class="zf-section-body zf-stack">
      <div class="zf-fgrid" style="--cols: repeat(3, minmax(0,1fr))">
        <div class="zf-field">
          <span class="zf-flabel">Location <span class="req">*</span>
            <span class="meta">· stock leaves the location picked here</span>
          </span>
          <select class="form-select" [(ngModel)]="payload.locationId" (ngModelChange)="onLocationChange()"
            [disabled]="isViewMode">
            <option [ngValue]="null">-- Select Location --</option>
            @for (loc of locationList; track loc.id) {
            <option [ngValue]="loc.id">{{ loc.name }}</option>
            }
          </select>
        </div>

        <div class="zf-field">
          <span class="zf-flabel">Write-off no
            @if (!isEditMode && !isViewMode) { <span class="meta">· provisional</span> }
          </span>
          <input class="form-control zf-mono-input" [(ngModel)]="payload.writeOffNumber" readonly
            [title]="isEditMode || isViewMode ? '' : 'Provisional — the final number is assigned when you save'" />
        </div>

        <div class="zf-field">
          <span class="zf-flabel">Notes</span>
          <input class="form-control" [(ngModel)]="payload.notes" placeholder="Reference, incident or audit no."
            [readonly]="isViewMode" />
        </div>
      </div>
    </div>
    }
  </div>

  <!-- ── Write-off lines ────────────────────────────────────────────── -->
  <div class="zf-doc-section zf-lines-card">
    <div class="zf-lines-head">
      <h2>Write-off lines</h2>
      <span class="zf-count">{{ lineCountLabel }}</span>
      <span class="zf-units">{{ getTotalQuantity() | number:'1.0-3' }} units</span>
      <div class="zf-pills">
        <button type="button" class="zf-pill" [class.on]="lineFilter === 'all'" (click)="lineFilter = 'all'">
          All<span class="n">{{ displayItems.length }}</span>
        </button>
        <button type="button" class="zf-pill" [class.on]="lineFilter === 'over'" (click)="lineFilter = 'over'">
          Over stock<span class="n">{{ overStockCount }}</span>
        </button>
        @if (lineFilter !== 'all' || lineQuery) {
        <button type="button" class="zf-pill clear" (click)="clearLineFilters()">✕ Clear filters</button>
        }
      </div>

      <div class="zf-lines-tools">
        <label class="zf-search" style="width:160px">
          <i class="bi bi-search"></i>
          <input [(ngModel)]="lineQuery" [ngModelOptions]="{ standalone: true }" placeholder="Filter these lines" />
        </label>

        @if (!isViewMode) {
        <div class="zf-tool" style="width:150px" title="Search by SKU">
          <span class="zf-tool-k mono">SKU</span>
          <ng-select [items]="skuSearchResults" bindLabel="sku" [(ngModel)]="selectedProduct"
            [ngModelOptions]="{ standalone: true }" (searchInput)="onSkuInput($event)" [loading]="isSkuSearching"
            (change)="onSearchResultSelected($event)" [disabled]="!payload.locationId"
            [placeholder]="payload.locationId ? 'Type SKU to search' : 'Select a location first'"
            [virtualScroll]="true" [typeahead]="skuSearch$">
            <ng-template ng-option-tmp let-item="item">
              <div class="ss-row">
                <span class="ss-row__primary zf-mono">{{ item.sku }}</span>
                <span class="ss-row__secondary">{{ item.longDescription || item.name }}</span>
              </div>
            </ng-template>
          </ng-select>
        </div>

        <div class="zf-tool" style="width:150px" title="Search by EAN">
          <span class="zf-tool-k mono">EAN</span>
          <ng-select [items]="eanSearchResults" bindLabel="matchedEan" [(ngModel)]="selectedProduct"
            [ngModelOptions]="{ standalone: true }" (searchInput)="onEanInput($event)" [loading]="isEanSearching"
            (change)="onSearchResultSelected($event)" [disabled]="!payload.locationId"
            [placeholder]="payload.locationId ? 'Type / scan EAN' : 'Select a location first'"
            [virtualScroll]="true" [typeahead]="eanSearch$">
            <ng-template ng-label-tmp let-item="item">{{ item.matchedEan || item.ean || item.sku }}</ng-template>
            <ng-template ng-option-tmp let-item="item">
              <div class="ss-row">
                <span class="ss-row__primary zf-mono">{{ item.matchedEan }}</span>
                <span class="ss-row__secondary">{{ item.sku }} · {{ item.longDescription || item.name }}</span>
              </div>
            </ng-template>
          </ng-select>
        </div>

        <div class="zf-tool" style="width:230px" title="Search by name">
          <span class="zf-tool-k"><i class="bi bi-plus-lg"></i></span>
          <ng-select [items]="nameSearchResults" bindLabel="name" [(ngModel)]="selectedProduct"
            [ngModelOptions]="{ standalone: true }" (searchInput)="onNameInput($event)" [loading]="isNameSearching"
            (change)="onSearchResultSelected($event)" [disabled]="!payload.locationId"
            [placeholder]="payload.locationId ? 'Type product name' : 'Select a location first'"
            [virtualScroll]="true" [typeahead]="nameSearch$">
            <ng-template ng-option-tmp let-item="item">
              <div class="ss-row">
                <span class="ss-row__primary">{{ item.name }}</span>
                <span class="ss-row__secondary">{{ item.sku }}</span>
              </div>
            </ng-template>
          </ng-select>
        </div>

        <button type="button" class="btn btn-secondary" (click)="clearWriteOffLines()" [disabled]="!displayItems.length">
          Clear lines
        </button>
        }
      </div>
    </div>

    <!-- Add-line strip: appears once a product is picked -->
    @if (selectedProduct && !isViewMode) {
    <div class="zf-add-strip">
      <div class="zf-add-prod">
        <span class="k">Adding</span>
        <span class="n" [title]="selectedProduct.longDescription || selectedProduct.name || selectedProduct.sku">
          {{ selectedProduct.longDescription || selectedProduct.name || selectedProduct.shortDescription || selectedProduct.sku }}
        </span>
        <span class="s">
          SKU {{ selectedProduct.sku || '—' }}
          · Stock {{ (pickedVariant?.soh ?? 0) | number:'1.0-3' }}
          @if (pickedBatch) { · Batch {{ pickedBatch }} }
          · Cost ₹{{ (pickedPricing?.costPrice ?? 0) | number:'1.2-2' }}
        </span>
      </div>
      <div class="zf-field w-90">
        <span class="zf-flabel">Qty</span>
        <input type="number" class="form-control" [(ngModel)]="addQty" [min]="1" [max]="selectedBatch?.quantity"
          (keydown.enter)="addItem()" />
      </div>
      <button type="button" class="btn btnprimary" (click)="addItem()" [disabled]="!selectedProduct || addQty <= 0">
        Add line
      </button>
      <button type="button" class="btn zf-btn-ghost" (click)="clearSelectedProduct()" title="Cancel">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>
    }

    @if (!isViewMode && selectedLocationName) {
    <div class="zf-scoped">
      <span class="k">Scoped</span>
      <span>Stock on hand is read at <strong>{{ selectedLocationName }}</strong> · quantity cannot exceed stock on hand</span>
      <span class="hint">Line value is calculated on cost price</span>
    </div>
    }

    @if (overStockCount) {
    <div class="zf-alert danger swo-blocked">
      <i class="bi bi-exclamation-triangle-fill"></i>
      <span>
        <strong>{{ overStockCount }} {{ overStockCount === 1 ? 'line has' : 'lines have' }}</strong>
        a quantity above the stock on hand. Fix those rows before submitting.
      </span>
    </div>
    }

    @if (displayItems.length) {
    <div class="zf-sheet-wrap">
      <table class="zf-sheet">
        <thead>
          <tr>
            <th class="st" style="--l:0; width:44px">#</th>
            <th class="st" style="--l:44px; width:100px">SKU code</th>
            <th class="st st-end" style="--l:144px; min-width:220px">Description</th>
            <th style="width:104px">Batch</th>
            <th class="num" style="width:96px">Quantity</th>
            <th class="num" style="width:82px">Stock</th>
            <th class="num" style="width:96px">Cost price</th>
            <th class="num" style="width:104px">Selling price</th>
            <th class="num" style="width:84px">MRP</th>
            <th class="num" style="width:116px">Total value</th>
            @if (!isViewMode) {
            <th class="ctr" style="width:60px">Action</th>
            }
          </tr>
        </thead>

        <tbody>
          @for (row of visibleItems; track row.item) {
          <tr [class.is-over]="isOverStock(row.item)">
            <td class="st zf-idx" style="--l:0">{{ row.index + 1 }}</td>
            <td class="st" style="--l:44px"><span class="zf-sku">{{ row.item.sku }}</span></td>
            <td class="st st-end zf-name" style="--l:144px">
              <span class="n" [title]="row.item.description || row.item.name">{{ row.item.description || row.item.name }}</span>
              <span class="s" [class.over]="isOverStock(row.item)">
                {{ isOverStock(row.item) ? 'OVER STOCK' : (row.item.batchNumber ? 'BATCH ' + row.item.batchNumber : 'WRITE-OFF') }}
              </span>
            </td>
            <td class="dim">{{ row.item.batchNumber || '—' }}</td>
            <td class="num">
              <input type="number" class="zf-cell-in swo-qty-in" [id]="'qty-' + row.index" [class.over]="isOverStock(row.item)"
                [(ngModel)]="row.item.quantity" min="1" (change)="calculateItemTotal(row.item)"
                (keyup)="calculateItemTotal(row.item)" [readonly]="isViewMode" />
            </td>
            <td class="num ink3">{{ row.item.stock | number : "1.0-3" }}</td>
            <td class="num">{{ row.item.costPrice | number : "1.2-2" }}</td>
            <td class="num ink3">{{ row.item.sellingPrice | number : "1.2-2" }}</td>
            <td class="num ink3">{{ row.item.mrp | number : "1.2-2" }}</td>
            <td class="num strong">{{ row.item.quantity * row.item.costPrice | number : "1.2-2" }}</td>
            @if (!isViewMode) {
            <td class="ctr">
              <button type="button" class="zf-del" title="Remove line" (click)="removeItem(row.index)">
                <i class="bi bi-trash3"></i>
              </button>
            </td>
            }
          </tr>
          }
          @if (!visibleItems.length) {
          <tr>
            <td [attr.colspan]="isViewMode ? 10 : 11" class="zf-sheet-empty">
              <div>
                <span class="t">No lines match this filter</span>
                <span class="s">Clear the filters to see all {{ displayItems.length }} lines.</span>
              </div>
            </td>
          </tr>
          }
        </tbody>

        <tfoot>
          <tr>
            <td class="st" colspan="3" style="--l:0"><span class="k">TOTAL</span>{{ lineCountLabel }}</td>
            <td></td>
            <td class="num">{{ getTotalQuantity() | number:'1.0-3' }}</td>
            <td></td>
            <td class="dim num" colspan="3">Grand total · cost price</td>
            <td class="num strong">₹ {{ getGrandTotal() | number : "1.2-2" }}</td>
            @if (!isViewMode) {
            <td></td>
            }
          </tr>
        </tfoot>
      </table>
    </div>
    } @else {
    <div class="zf-sheet-blank">
      <span class="t">No items on this write-off yet</span>
      <span class="s">
        @if (isViewMode) { This write-off has no lines. }
        @else if (!payload.locationId) { Pick a location in the header, then search a SKU, EAN or product name above to add it. }
        @else { Search a SKU, EAN or product name above to add it manually, or upload a list from the write-offs page. }
      </span>
    </div>
    }
  </div>

  <!-- ── Write-off value strip ──────────────────────────────────────── -->
  <div class="zf-order-strip">
    <div class="zf-strip-info">
      <div class="zf-strip-line">
        <span class="dot" [class.ok]="displayItems.length && !overStockCount"></span>
        {{ lineCountLabel }} · {{ getTotalQuantity() | number:'1.0-3' }} units
        @if (overStockCount) { · {{ overStockCount }} over stock }
      </div>
      <div class="zf-strip-sub">
        @if (!displayItems.length) {
        <span>Add at least one item to save a draft or submit.</span>
        } @else if (isViewMode) {
        <span>Stock left <strong>{{ selectedLocationName || 'the location' }}</strong> and posted to the write-off ledger.</span>
        } @else {
        <span>Submitting permanently removes this stock from <strong>{{ selectedLocationName || 'the selected location' }}</strong> · totals stay pinned to the grid</span>
        }
      </div>
    </div>
    <div class="zf-strip-kpis">
      <div class="zf-kpi">
        <span class="k">Total quantity</span>
        <span class="v">{{ getTotalQuantity() | number:'1.0-3' }}</span>
      </div>
      <div class="zf-kpi">
        <span class="k">At selling price</span>
        <span class="v">₹{{ totalAtSellingPrice | number:'1.2-2' }}</span>
      </div>
      <div class="zf-kpi">
        <span class="k">At MRP</span>
        <span class="v">₹{{ totalAtMrp | number:'1.2-2' }}</span>
      </div>
      <div class="zf-kpi dark">
        <span class="k">Booked value · cost price</span>
        <span class="v">₹{{ getGrandTotal() | number:'1.2-2' }}</span>
      </div>
    </div>
  </div>
</div>
